import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import {
  ADD_EWALLET_ID_NAME,
  ADD_EWALLET_NAME,
  ADD_EWALLET_NUMBER_NAME,
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
} from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';

const EWALLETS_SEARCH_NAME = 'ewallets-search-field';

const CustomEwalletTypeDropdown = ({
  config,
  ewallets,
  ewalletExisteds,
  information,
  setInformation,
  validation,
  setValidation,
  currentEwalletSelected,
  setCurrentEwalletSelected,
  checkEwalletAccountExisted,
}) => {
  const { t } = useLang();

  const [searchValue, setSearchValue] = useState('');
  const [ewalletsSearch, setEwalletsSearch] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);
  const [isChecking, setChecking] = useState(false);

  const isSearchInvalid = !searchTextValid(searchValue);

  const numberMaxLength = config?.max;
  const numberMinLength = config?.min;

  useInputFocus(EWALLETS_SEARCH_NAME);

  useEffect(() => {
    const accountNumber = information[ADD_EWALLET_NUMBER_NAME];
    const isCheckNumber =
      accountNumber?.length <= numberMaxLength &&
      accountNumber?.length >= numberMinLength &&
      !validation[ADD_EWALLET_NUMBER_NAME];

    if (isCheckNumber && isChecking) {
      handleNumberValid(
        accountNumber,
        ADD_EWALLET_NUMBER_NAME,
        validation,
        information,
        currentEwalletSelected?.id
      );
    }
  }, [isChecking, currentEwalletSelected]);

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setEwalletsSearch(null);

      return;
    }

    const newEwalletSearch = ewallets?.filter(
      (ewallet) =>
        ewallet?.name?.toLowerCase()?.includes(value?.toLowerCase()) && ewallet
    );
    setEwalletsSearch(newEwalletSearch);
  };

  const handleNumberValidation = async (
    value,
    name,
    validation,

    information,

    ewalletId
  ) => {
    return validation;
  };
  const checkNumberValid = async (
    value,
    name,
    validation,
    information,
    ewalletId
  ) => {
    if (!validation[name]) {
      validation = await handleNumberValidation(
        value,
        name,
        validation,
        information,
        ewalletId
      );
    }

    setValidation(validation);
  };
  const { debounce: handleNumberValid } = useDebounceCallback(
    (value, name, validation, information, ewalletId) => {
      checkNumberValid(value, name, validation, information, ewalletId);
    },
    DEBOUNCE_SEC
  );

  const { debounce: handleAllowCheckEwalletNumber } = useDebounceCallback(
    (validation) => {
      if (validation[ADD_EWALLET_NUMBER_NAME]) {
        setChecking(false);
      } else {
        setChecking(true);
      }
    },
    DEBOUNCE_SEC
  );

  const handleEwalletSelected = (ewallet) => {
    setCurrentEwalletSelected(ewallet);

    setSearchValue('');

    setEwalletsSearch(null);

    setShowDropdown(false);

    const newInformation = {
      ...information,
      [ADD_EWALLET_NAME]: ewallet.name,
      [ADD_EWALLET_ID_NAME]: ewallet.id,
      [ADD_EWALLET_NUMBER_NAME]: '',
    };

    let newValidation = {
      ...validation,
      [ADD_EWALLET_NUMBER_NAME]: '',
    };

    newValidation = checkEwalletAccountExisted(
      ewalletExisteds,
      newInformation,
      newValidation
    );

    if (newValidation[ADD_EWALLET_NUMBER_NAME].includes('exist')) {
      setValidation(newValidation);

      return;
    }

    if (
      newInformation[ADD_EWALLET_NUMBER_NAME] &&
      !newValidation[ADD_EWALLET_NUMBER_NAME]
    ) {
      handleAllowCheckEwalletNumber(newValidation);
    }

    setInformation(newInformation);

    setValidation(newValidation);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');
    setEwalletsSearch(null);
    setShowDropdown(!isShowDropdown);
  };

  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        name={EWALLETS_SEARCH_NAME}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{ borderRadius: '0 0 8px 8px', paddingBottom: 0 }}
      >
        {renderDropdownItems(isSearchInvalid ? [] : ewalletsSearch || ewallets)}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
        >
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{t('form_text_no_result')}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.id}
            eventKey={index}
            onClick={() => handleEwalletSelected(item)}
          >
            {item.name}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  return (
    <EwalletDropdownStyled>
      <Label>
        {t('form_ewallet_type')}
        <span>*</span>
      </Label>
      <DropdownCommon
        value={currentEwalletSelected?.name}
        placeholder={'form_placeholder_please_select'}
        keyError={validation?.[ADD_EWALLET_NAME]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
    </EwalletDropdownStyled>
  );
};

const EwalletDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  margin-bottom: 8px;

  & span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

export default CustomEwalletTypeDropdown;
